import type {
  VehicleExpirationFilterVariables,
  VehicleListFilterVariables,
} from '@/modules/vehicles'
import { endOfDay, startOfDay } from 'date-fns'
import type { DateRange } from 'react-day-picker'
import type { VehicleListFilterForm } from './types'

/**
 * Map the filters every vehicle list shares to query variables, dropping
 * everything that is not set so an untouched filter sends no arguments at all.
 */
export function getVehicleListFilterVariables(
  filter: VehicleListFilterForm
): VehicleListFilterVariables {
  const q = filter.q.trim()

  return {
    q: q || undefined,
    vehicles: filter.vehicles.length > 0 ? filter.vehicles : undefined,
    responsibleUsers:
      filter.responsibleUsers.length > 0 ? filter.responsibleUsers : undefined,
  }
}

/**
 * The shared variables plus the ones only a list of expiring rows has: the
 * derived status and the deadline window. The services page maps the same
 * `dateRange` onto its scheduled-slot arguments instead.
 */
export function getVehicleExpirationFilterVariables(
  filter: VehicleListFilterForm
): VehicleExpirationFilterVariables {
  const { from, to } = getDateRangeBounds(filter.dateRange)

  return {
    ...getVehicleListFilterVariables(filter),
    expirationStatus: filter.expirationStatus || undefined,
    expirationDateGte: from,
    expirationDateLte: to,
  }
}

/**
 * The picked range as ISO bounds, widened to whole days: picking today as the
 * end of the range should include a record dated later today.
 */
export function getDateRangeBounds(range: DateRange | null): {
  from: string | undefined
  to: string | undefined
} {
  return {
    from: range?.from ? startOfDay(range.from).toISOString() : undefined,
    to: range?.to ? endOfDay(range.to).toISOString() : undefined,
  }
}

/**
 * Whether any of the shared filters is narrowing the list — drives the "clear"
 * button. Pages with extra filters OR their own state into this.
 */
export function hasActiveVehicleListFilter(
  filter: VehicleListFilterForm
): boolean {
  return (
    filter.q.trim().length > 0 ||
    filter.vehicles.length > 0 ||
    filter.responsibleUsers.length > 0 ||
    filter.expirationStatus !== '' ||
    Boolean(filter.dateRange?.from || filter.dateRange?.to)
  )
}
